import { useMemo } from 'react';
import { useMeasure } from 'react-use';
import { Content } from 'src/contexts/ContentContext';
import { Switch } from 'src/core/interim/Switch';
import { PosFormField } from 'src/core/POS/PosFormField';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { Listing, MergeListingGroupInput } from 'src/WebApiController';

import { GroupsPreview } from '../GroupListingsV2/components/GroupsPreview';
import { EventGroupings } from './components/EventGroupings';
import { GroupListingsBodyProps } from './components/groupingTypes';
import { flattenListingGroup } from './components/groupingUtils';
import { useFullySoldFilter } from './GroupListingsBody.hook';

const listingsAvailableForGrouping =
  (
    listingGroupId?: string | null,
    listingGroupInput?: MergeListingGroupInput
  ) =>
  (listing: Listing): boolean => {
    if (listing?.id < 0) {
      return false;
    }
    // Include ungrouped listings
    if (listing.ltGrpId == null) {
      return true;
    }
    // Include listings in the group
    if (listing.ltGrpId === listingGroupId) {
      return true;
    }

    // Include listings in the group items
    // This can happen when adding new listings to the group
    const groupItem = listingGroupInput?.listingGroupItems?.find(
      (item) => item.listingId === listing.id
    );
    return Boolean(groupItem);
  };

export const GroupListingsBody = ({
  events,
  listingGroup,
  onEventSelected,
}: GroupListingsBodyProps) => {
  const {
    excludeFullySold,
    showExcludeFullySold,
    sanitizedEvents,
    mergeListingGroupInputs,
    onToggleExcludeFullySold,
  } = useFullySoldFilter(events, listingGroup);

  const [containerRef, { height: containerHeight }] =
    useMeasure<HTMLDivElement>();

  const groupListings = useMemo(() => {
    if (listingGroup && mergeListingGroupInputs?.length === 1) {
      return (
        sanitizedEvents
          .flatMap(
            ({ entities }) =>
              entities.listings
                ?.flatMap(flattenListingGroup)
                .map((l) => l as Listing) ?? []
          )
          .filter(
            listingsAvailableForGrouping(
              listingGroup.ltGrpId,
              mergeListingGroupInputs[0]
            )
          ) ?? []
      );
    }
    return [];
  }, [listingGroup, mergeListingGroupInputs, sanitizedEvents]);

  return (
    <Stack
      direction="column"
      gap={listingGroup ? undefined : 'xl'}
      height="full"
    >
      {showExcludeFullySold && (
        <PosFormField
          label={<Content id={ContentId.ExcludeFullySoldListings} />}
        >
          <Switch
            checked={excludeFullySold}
            onChange={onToggleExcludeFullySold}
          />
        </PosFormField>
      )}
      <Stack
        ref={containerRef}
        direction="column"
        style={{ height: listingGroup ? '100%' : '90%' }}
      >
        {listingGroup ? (
          <GroupsPreview
            eventListings={groupListings}
            groups={mergeListingGroupInputs}
          />
        ) : (
          <EventGroupings
            events={sanitizedEvents}
            onEventSelected={onEventSelected}
            containerHeight={containerHeight}
          />
        )}
      </Stack>
    </Stack>
  );
};
